import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDownload } from 'react-icons/hi';
import { Modal, Button, Input } from '@/components/common';
import { Supplier } from '@/types/supplier';
import { WarehouseReceipt } from '@/types/warehouseReceipt';

interface SupplierDebtExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Supplier | null;
  warehouseReceipts: WarehouseReceipt[];
  onExport: (dateFrom: string, dateTo: string) => Promise<void>;
  initialDateFrom?: string;
  initialDateTo?: string;
}

const SupplierDebtExportModal: React.FC<SupplierDebtExportModalProps> = ({
  isOpen,
  onClose,
  supplier,
  onExport,
  initialDateFrom,
  initialDateTo,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Set date range from parent (debt tab) or fall back to current month
  React.useEffect(() => {
    if (isOpen) {
      if (initialDateFrom && initialDateTo) {
        // Convert ISO string to date input format (YYYY-MM-DD)
        setDateFrom(initialDateFrom.split('T')[0]);
        setDateTo(initialDateTo.split('T')[0]);
      } else if (!dateFrom && !dateTo) {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setDateFrom(firstDay.toISOString().split('T')[0]);
        setDateTo(lastDay.toISOString().split('T')[0]);
      }
    }
  }, [isOpen, initialDateFrom, initialDateTo]);

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      return;
    }

    setLoading(true);
    try {
      await onExport(dateFrom, dateTo);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDateFrom('');
    setDateTo('');
    onClose();
  };

  const isValidDateRange = dateFrom && dateTo && new Date(dateFrom) <= new Date(dateTo);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('suppliers.debtExport.title')}
      size="md"
      footer={
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            size="md"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleExport}
            disabled={!isValidDateRange || loading}
            loading={loading}
            icon={<HiDownload className="h-4 w-4" />}
          >
            {t('suppliers.debtExport.export')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Supplier Information */}
        {supplier && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              {t('suppliers.debtExport.supplierInfo')}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('suppliers.fields.name')}:</span>
                <span className="ml-2 font-medium">{supplier.name}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('suppliers.fields.code')}:</span>
                <span className="ml-2 font-medium">{supplier.code}</span>
              </div>
            </div>
          </div>
        )}

        {/* Date Range Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">
            {t('suppliers.debtExport.dateRange')}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.debtExport.dateFrom')}
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.debtExport.dateTo')}
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {!isValidDateRange && dateFrom && dateTo && (
            <p className="text-sm text-red-600">
              {t('suppliers.debtExport.invalidDateRange')}
            </p>
          )}
        </div>

        {/* Export Preview */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            {t('suppliers.debtExport.exportPreview')}
          </h3>
          <div className="text-sm text-blue-800">
            <p>{t('suppliers.debtExport.exportDescription')}</p>
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li>{t('suppliers.debtExport.includeHeaders')}</li>
              <li>{t('suppliers.debtExport.includeSupplierInfo')}</li>
              <li>{t('suppliers.debtExport.includeReceiptDetails')}</li>
              <li>{t('suppliers.debtExport.includeLineItems')}</li>
              <li>{t('suppliers.debtExport.includeSignature')}</li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SupplierDebtExportModal;
